/**
 * Recordings after the festival — full sets, excerpts, aftermovies (plan.md
 * step 58). Pure, so the API that classifies a candidate and the app that
 * plays one agree.
 *
 * **Embed, never host.** MyRave keeps a platform and an id; the sound and the
 * picture stay on YouTube, SoundCloud or Mixcloud, and are loaded only when a
 * visitor presses play (`embedUrl`).
 *
 * **Whether a recording is the full set is decided here, from its length
 * against the scheduled set — never from a title.** A 1:48 upload for a
 * two-hour slot is likely the set; a four-minute clip is not, whatever it is
 * called.
 */

export const RECORDING_PLATFORMS = ['YOUTUBE', 'SOUNDCLOUD', 'MIXCLOUD', 'OTHER'] as const;
export type RecordingPlatform = (typeof RECORDING_PLATFORMS)[number];

export const RECORDING_KINDS = ['FULL_SET', 'EXCERPT', 'AFTERMOVIE', 'VIDEO'] as const;
export type RecordingKind = (typeof RECORDING_KINDS)[number];

/** A recording as the bundle ships it. No thumbnail: it would be a third-party request before play. */
export interface Recording {
  readonly id: string;
  readonly actId?: string;
  readonly kind: RecordingKind;
  readonly platform: RecordingPlatform;
  readonly externalId: string;
  readonly url: string;
  readonly title: string;
  readonly durationSec?: number;
  readonly official: boolean;
}

/**
 * An artist's full set from an EARLIER year of the same rave (the user,
 * 2026-10-16: "in OZORA 2027, if someone checks Captain Hook's details, he can
 * watch last year's set as well") — in the bundle as `pastSets`, so a set's
 * card plays it, offline listed, nothing loaded before Play.
 */
export interface PastSet extends Recording {
  readonly artistId: string;
  /** "O.Z.O.R.A 2026" — the year it was played. */
  readonly editionName: string;
}

/** Which platform an address is, and the id that names the recording there. */
export function parseRecordingUrl(raw: string): { platform: RecordingPlatform; externalId: string } | null {
  let url: URL;
  try { url = new URL(raw); } catch { return null; }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
  const host = url.hostname.replace(/^(www|m|music)\./, '').toLowerCase();
  const path = url.pathname.replace(/\/+$/, '');
  if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
    const v = url.searchParams.get('v');
    const id = v ?? path.match(/^\/(?:live|embed|shorts)\/([\w-]{6,})/)?.[1];
    return id && /^[\w-]{6,20}$/.test(id) ? { platform: 'YOUTUBE', externalId: id } : null;
  }
  if (host === 'youtu.be') {
    const id = path.slice(1);
    return /^[\w-]{6,20}$/.test(id) ? { platform: 'YOUTUBE', externalId: id } : null;
  }
  if (host === 'soundcloud.com') {
    const parts = path.split('/').filter(Boolean);
    return parts.length === 2 ? { platform: 'SOUNDCLOUD', externalId: parts.join('/').toLowerCase() } : null;
  }
  if (host === 'mixcloud.com') {
    const parts = path.split('/').filter(Boolean);
    return parts.length === 2 ? { platform: 'MIXCLOUD', externalId: parts.join('/').toLowerCase() } : null;
  }
  return null;
}

/** A SoundCloud address whose second segment is a page of the account, never one track. */
const SOUNDCLOUD_PAGES = new Set(['sets', 'tracks', 'albums', 'likes', 'reposts', 'popular-tracks', 'followers', 'following', 'comments', 'spotlight']);

/**
 * The SoundCloud track an artist's sample is (the user, 2026-11-02: "preview of artists samples should come from
 * SoundCloud — best track from each artist"): `user/track` for a track's address, else null. The API plays its
 * 30-second preview at `/samples/soundcloud/<user>/<track>` — the address SoundCloud gives expires in minutes, so it
 * is never stored.
 */
export function soundcloudSamplePath(audioUrl: string | null | undefined): string | null {
  const r = audioUrl ? parseRecordingUrl(audioUrl) : null;
  if (r?.platform !== 'SOUNDCLOUD') return null;
  const [user, track] = r.externalId.split('/');
  return user && track && /^[\w-]+$/.test(user) && /^[\w-]+$/.test(track) && !SOUNDCLOUD_PAGES.has(track) ? `${user}/${track}` : null;
}

/** A SoundCloud PROFILE address (`soundcloud.com/<user>`): its user, else null. */
export function soundcloudProfileUser(url: string | null | undefined): string | null {
  let u: URL;
  try { u = new URL(url ?? ''); } catch { return null; }
  if (!/^https?:$/.test(u.protocol) || u.hostname.replace(/^(www|m)\./, '').toLowerCase() !== 'soundcloud.com') return null;
  const parts = u.pathname.split('/').filter(Boolean);
  return parts.length === 1 && /^[\w-]+$/.test(parts[0]!) ? parts[0]!.toLowerCase() : null;
}

/** A profile with nothing playable is looked at again after this. */
export const SAMPLE_RECHECK_MS = 30 * 24 * 60 * 60_000;

/**
 * The artist's SoundCloud profile while its best track is still to be looked for — never looked for, or looked for
 * with nothing found over `SAMPLE_RECHECK_MS` ago; else null.
 */
export function profileToSample(links: unknown, checkedAt: Date | string | null | undefined, now: number): string | null {
  const profile = links && typeof links === 'object' ? (links as Record<string, unknown>).soundcloud : null;
  if (typeof profile !== 'string' || !soundcloudProfileUser(profile)) return null;
  return checkedAt && now - new Date(checkedAt).getTime() < SAMPLE_RECHECK_MS ? null : profile;
}

/**
 * Whether the raves' OWN sample files play (an imported MP3, an "Audio link" that is not SoundCloud). Off for now (the
 * user, 2026-11-02: "disable the old samples for now") — only SoundCloud plays.
 */
export const OLD_SAMPLES_PLAY = false;

/**
 * The sample to play of an act or artist: a SoundCloud track first (at any level); else the artist's SoundCloud
 * PROFILE while its best track is not known yet (`profile` — the API finds and saves it on the first play, the user
 * 2026-11-02: "it should be filled automatically while users are browsing"); else — while `OLD_SAMPLES_PLAY` — the
 * first address there is.
 */
export function pickSample(urls: readonly (string | null | undefined)[], profile?: string | null): string | undefined {
  return urls.find((u) => soundcloudSamplePath(u)) ?? (soundcloudProfileUser(profile) ? profile! : undefined)
    ?? (OLD_SAMPLES_PLAY ? urls.find((u) => u) ?? undefined : undefined);
}

/**
 * The player, privacy-enhanced where the platform offers it. Loaded ONLY on a
 * press of play: before that, no request reaches the platform.
 */
export function embedUrl(platform: RecordingPlatform, externalId: string): string | null {
  switch (platform) {
    case 'YOUTUBE': return `https://www.youtube-nocookie.com/embed/${encodeURIComponent(externalId)}?autoplay=1`;
    case 'SOUNDCLOUD': return `https://w.soundcloud.com/player/?url=${encodeURIComponent(`https://soundcloud.com/${externalId}`)}&auto_play=true`;
    case 'MIXCLOUD': return `https://player-widget.mixcloud.com/widget/iframe/?hide_cover=1&autoplay=1&feed=${encodeURIComponent(`/${externalId}/`)}`;
    default: return null;
  }
}

/**
 * The recording's own picture, for its card (the QA walk, w-sets-list, 2026-11-02: "missing thumbs on the full set").
 * YouTube's still is a fixed address by the video's id (`i.ytimg.com`, an image host — no player, no script, no
 * cookie); SoundCloud and Mixcloud have none without asking their APIs, so null (the card shows the artist's photo).
 */
export function thumbnailUrl(platform: RecordingPlatform, externalId: string): string | null {
  if (platform !== 'YOUTUBE' || !/^[\w-]{6,20}$/.test(externalId)) return null;
  return `https://i.ytimg.com/vi/${externalId}/hqdefault.jpg`;
}

export interface Candidate {
  title: string;
  durationSec: number | null;
  /** Was the length read from the platform itself, or only reported? */
  durationVerified: boolean;
  official: boolean;
  publishedAt: Date | null;
}

export interface Expected {
  artistName: string;
  festivalName: string;
  /** The scheduled set. */
  startAt: Date;
  endAt: Date;
}

export interface Classification {
  kind: 'FULL_SET' | 'EXCERPT';
  confidence: 'LOW' | 'MEDIUM' | 'HIGH';
  reasons: string[];
  /**
   * Published by itself, no person asked (the user, 2026-10-16: "if the set
   * is matched by name it can be automatically published"): a FULL SET (its
   * length fits the slot) whose title names BOTH the artist and the
   * festival, and that was not uploaded before the set was played.
   */
  autoPublish: boolean;
}

const words = (s: string) => s.toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '').replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
/**
 * Does the title name it? As words ("Shpongle" in "Shpongle @ OZORA"), or —
 * for a name written with dots or spaces, "O.Z.O.R.A" — with every separator
 * gone on both sides ("ozora" in "ozorafestival2026"; OZORA's stored name,
 * 2026-10-16). Three letters at least for the second, so "A.B" names nothing.
 */
/**
 * A compilation or a highlights video, not somebody's set — said by its title
 * (the OZORA 2026 sweep: "BEST OF OZORA Festival 2026 … ft Tristan, Vini
 * Vici…", "Top Tracks … ft Astrix", "… | Best Psytrance Moments" were each
 * published as one artist's full set).
 */
export function isCompilationTitle(title: string): boolean {
  const t = ` ${words(title)} `;
  return [' best of ', ' best tracks ', ' best moments ', ' moments ', ' top tracks ', ' top ', ' ft ', ' feat ', ' featuring ',
    ' selection ', ' compilation ', ' iconic sets ', ' highlights ', ' recap ', ' playlist ']
    .some((w) => t.includes(w));
}

/**
 * A record label's channel ("Reversible Records", "Iboga Recordings", "Nano
 * Music") — the user, 2026-10-16: "labels go in if it's official, like you
 * added" (Ninesense's OZORA set is on his label's channel).
 */
export function isLabelChannel(uploader: string): boolean {
  return /\b(records|recordings|label|music|productions|records?\s+ltd|audio)\b/i.test(uploader);
}

/**
 * Who may have put a recording up (the user, 2026-10-16: "if it's not posted by
 * the artist or the rave's account, it's trash"; then labels): the artist's own
 * channel, the rave's, or a record label's. ONE rule — the sweep and the
 * proposal check both ask it.
 */
export function officialUploader(uploader: string | null | undefined, artistName: string | null, festivalName: string): boolean {
  if (!uploader) return false;
  return titleNames(uploader, festivalName)
    || (artistName !== null && (titleNames(uploader, artistName) || titleNames(artistName, uploader)))
    || isLabelChannel(uploader);
}

export function titleNames(title: string, name: string): boolean {
  const w = words(name);
  if (!w) return false;
  if (words(title).includes(w)) return true;
  const compact = w.replace(/ /g, '');
  return compact.length >= 3 && words(title).replace(/ /g, '').includes(compact);
}
const minutes = (sec: number) => `${Math.floor(sec / 3600)}:${String(Math.round((sec % 3600) / 60)).padStart(2, '0')}`;

/**
 * Full set or excerpt, and how sure — from checks, each written down.
 *
 * Length is the deciding check: 75 %–150 % of the scheduled slot, and at
 * least 20 minutes. Then the title names the artist, the festival, the
 * year; it was published after the set; the channel is the artist's or the
 * festival's own. A length nobody verified caps the confidence at MEDIUM.
 */
export function classifyRecording(c: Candidate, e: Expected): Classification {
  const reasons: string[] = [];
  const setSec = Math.max(60, (e.endAt.getTime() - e.startAt.getTime()) / 1000);
  let points = 0;
  let full = false;

  if (c.durationSec === null) {
    reasons.push('length unknown');
  } else {
    const ratio = c.durationSec / setSec;
    full = c.durationSec >= 20 * 60 && ratio >= 0.75 && ratio <= 1.5;
    reasons.push(`${minutes(c.durationSec)} long against a ${minutes(setSec)} set (${Math.round(ratio * 100)}%)${c.durationVerified ? '' : ', length not verified'}`);
    if (full) points += 2;
  }
  const title = words(c.title);
  const namesArtist = titleNames(c.title, e.artistName);
  const namesFestival = titleNames(c.title, e.festivalName);
  if (namesArtist) { points++; reasons.push('the title names the artist'); }
  if (namesFestival) { points++; reasons.push('the title names the festival'); }
  if (title.includes(String(e.startAt.getUTCFullYear()))) { points++; reasons.push('the title names the year'); }
  const tooEarly = Boolean(c.publishedAt && c.publishedAt.getTime() < e.startAt.getTime());
  if (tooEarly) { points -= 2; reasons.push('published BEFORE the set was played'); }
  if (c.official) { points++; reasons.push("the artist's or festival's own channel"); }

  let confidence: Classification['confidence'] = points >= 5 ? 'HIGH' : points >= 3 ? 'MEDIUM' : 'LOW';
  if (!full) confidence = confidence === 'HIGH' ? 'MEDIUM' : confidence;
  if (!c.durationVerified && confidence === 'HIGH') confidence = 'MEDIUM';
  const compilation = isCompilationTitle(c.title);
  if (compilation) reasons.push('the title reads as a compilation or highlights, not a set');
  const autoPublish = full && namesArtist && namesFestival && !tooEarly && !compilation;
  if (autoPublish) reasons.push('matched by name — published by itself');
  return { kind: full ? 'FULL_SET' : 'EXCERPT', confidence, reasons, autoPublish };
}
